<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <div>
        <h1 class="headline">User Access Settings</h1>
      </div>
      <v-col class="py-0">
        <list-filters
          :filters="filters"
          label="Find User Access Setting"
          ref-name="userAccessSettingsListFilters"
          @searchParamsChanged="searchParamsChanged"
          :expansionPanel="false"
        ></list-filters>
      </v-col>
      <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
      <div>
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
    </v-row>
    <v-row>
      <v-col>
        <template v-if="showAgGrid">
          <ag-grid-vue
            class="ag-theme-alpine"
            id="ag-user-access-settings-list"
            :columnDefs="agGridColumns"
            :rowData="items"
            :context="agContext"
            @grid-ready="onAgGridReady"
            @columnMoved="onAgColumnMoved"
            @columnResized="onAgColumnResized"
            @sortChanged="onAgSortChanged"
            @selection-changed="onAgSelectionChanged"
            @newColumnsLoaded="onAgNewColumns"
            suppressDragLeaveHidesColumns
          ></ag-grid-vue>
        </template>
        <v-data-table
          v-else
          :items="items"
          :loading="$apollo.loading"
          :search="search"
          item-key="id"
          class="elevation-1 it-list"
          v-bind="pagingOptions"
          mobile-break-point="1264"
          :show-select="!isViewOnly"
          v-model="selected"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :headers="filteredHeaders"
          show-group-by
          fixed-header
          hide-default-footer
          :height="gridHeight"
          @update:options="onListOptionsUpdate"
          @current-items="dataTableReady"
        >
          <template v-slot:item.setting_value="{ item }">
            <v-icon v-if="item.setting_value" color="success" small
              >mdi-check</v-icon
            >
            <v-icon v-else color="error" small>mdi-close</v-icon>
          </template>
          <template v-slot:item.setting_name="{ item }">
            {{ formatSettingName(item.setting_name) }}
          </template>
          <template v-slot:item.group_name="{ item }">
            {{
              item.group_name
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")
            }}
          </template>
          <template v-slot:footer>
            <list-actions
              v-if="selected.length > 0"
              :selected="selected"
              list="userAccessSettings"
              :columns="visibleColumns"
              :filter="searchParams"
              :hideExport="hideExport"
              hide-unexport
              show-sage
              @update="onListUpdate"
              @close="selected = []"
            >
              <v-btn
                fab
                dark
                small
                color="green"
                @click="onShowBatchUpdate"
                v-if="false"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-pencil</v-icon>
                  </template>
                  <span>Batch Update</span>
                </v-tooltip>
              </v-btn>

              <v-btn fab dark small color="green" @click="onActivateSetting">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-check</v-icon>
                  </template>
                  <span>Activate Setting</span>
                </v-tooltip>
              </v-btn>
              <v-btn fab dark small color="red" @click="onDeActivateSetting">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-close</v-icon>
                  </template>
                  <span>Deactivate Setting</span>
                </v-tooltip>
              </v-btn>
              <v-btn fab dark small color="green" @click="onActivateUser">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-account</v-icon>
                  </template>
                  <span>Activate User</span>
                </v-tooltip>
              </v-btn>
              <v-btn fab dark small color="red" @click="onDeActivateUser">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">mdi-account-off</v-icon>
                  </template>
                  <span>Deactivate User</span>
                </v-tooltip>
              </v-btn>
            </list-actions>
            <v-row justify="center" v-if="selected.length > 0">
              <h3 class="py-2 caption">
                {{ selected.length }} record(s) selected
              </h3>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="userAccessSettings"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('userAccessSettings')"
    ></get-more-results>
    <batch-update-dialog
      :show="showBatchUpdateDialog"
      :columns="editableColumns"
      @close="showBatchUpdateDialog = false"
      @update="onUpdateRecords"
      max-width="600"
    />
    <v-row v-if="debug">
      <v-col>
        <pre>{{ isViewOnly }}</pre>
        <pre>{{ items }}</pre>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { removeGQLTypename } from "../../../../helpers";
import { ListMixin } from "../../../../mixins/ListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter.vue";
import GetMoreResults from "../../shared/GetMoreResults.vue";
import ListFilters from "../../shared/list-filters/ListFilters.vue";
import userAccessSettingsFilters from "./access-settings-filter.js";
import ListActions from "../../shared/list-actions/ListActions.vue";
import BatchUpdateDialog from "../../shared/BatchUpdateDialog.vue";
import { UPDATE_RECORDS_MUTATION } from "../../../../constants/graphql";
import AgGridSwitch from "../../shared/AgGridSwitch.vue";
import { AgGridVue } from "ag-grid-vue";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
const LIST_QUERY = gql`
  query userAccessSettings(
    $filter: UserAccessSettingFilterType
    $after: String
    $sort: SortOrderType
    $first: Int
  ) {
    userAccessSettings(
      first: $first
      filter: $filter
      after: $after
      orderBy: $sort
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          group_name
          setting_name
          setting_value
          notes
          user {
            id
            username
            email
            name
            isActive
            agentName
          }
        }
      }
    }
  }
`;
export default {
  components: {
    ListVisibleColumnsAndFilter,
    GetMoreResults,
    ListFilters,
    ListActions,
    BatchUpdateDialog,
    AgGridSwitch,
    AgGridVue,
  },
  name: "UserAccessSettings",

  props: {
    refName: {
      type: String,
      default: () => "UserAccessSettingsList",
    },
    refNameColumns: {
      type: String,
      default: () => "UserAccessSettingsCols",
    },
  },

  mixins: [ListMixin, AgGridListMixin],

  data() {
    return {
      debug: false,
      selected: [],
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "groupName",
        "settingName",
        "settingValue",
        "username",
        "email",
        "actions",
      ]),
      headers: [
        {
          text: "Group Name",
          value: "group_name",
          name: "groupName",
          sortable: true,
          valueGetter: (payload) =>
            payload.data.group_name
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
        },
        {
          text: "Setting",
          value: "setting_name",
          name: "settingName",
          sortable: true,
          valueGetter: (payload) =>
            payload.data.setting_name
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
        },
        {
          text: "Value",
          value: "setting_value",
          name: "settingValue",
          sortable: true,
          valueGetter: (payload) => payload.data.setting_value,
        },
        {
          text: "Username",
          value: "user.username",
          name: "username",
          sortable: true,
          valueGetter: (payload) => payload.data.user.username,
        },
        {
          text: "Email",
          value: "user.email",
          name: "email",
          sortable: true,
          valueGetter: (payload) => payload.data.user.email,
        },
        {
          text: "Name",
          value: "user.name",
          name: "name",
          sortable: true,
          valueGetter: (payload) => payload.data.user.name,
        },
        {
          text: "Agent Name",
          value: "user.agentName",
          name: "agentName",
          sortable: true,
          valueGetter: (payload) => payload.data.user.agentName,
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
          sortable: true,
          valueGetter: (payload) => payload.data.notes,
        },
      ],
      userAccessSettingsFilters: userAccessSettingsFilters,
      editableColumns: [
        {
          label: "Setting Value",
          type: "BOOLEAN",
          name: "setting_value",
        },
      ],
      showBatchUpdateDialog: false,
    };
  },

  computed: {
    isViewOnly() {
      return this.$store.getters.hasAccessTo("plugins:access_module_view_only");
    },
    gridHeight() {
      if (!this.fixedHeader) {
        return null;
      }
      return this.items && this.items.length >= 20
        ? this.windowHeight - 270
        : null;
    },
    hideExport() {
      return false;
    },
    filters() {
      return this.userAccessSettingsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    items() {
      return this.userAccessSettings && this.userAccessSettings.edges
        ? this.userAccessSettings.edges.map(removeGQLTypename)
        : [];
    },
  },

  methods: {
    formatSettingName(settingName) {
      return settingName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    onActivateUser() {
      const records = [...new Set(this.selected.map((item) => item.user.id))];
      const attributes = [
        {
          name: "is_active",
          value: "true",
        },
      ];
      console.log("Activate User", records, attributes);
      this.updateUserRecords(records, attributes);
    },
    onDeActivateUser() {
      const records = [...new Set(this.selected.map((item) => item.user.id))];
      const attributes = [
        {
          name: "is_active",
          value: "false",
        },
      ];
      console.log("Deactivate User", records, attributes);
      this.updateUserRecords(records, attributes);
    },
    onActivateSetting() {
      console.log("Activate Setting");
      const records = this.selected.map((item) => item.id);
      const attributes = [
        {
          name: "setting_value",
          value: "true",
        },
      ];
      this.updateRecords(records, attributes);
    },
    onDeActivateSetting() {
      const records = this.selected.map((item) => item.id);
      const attributes = [
        {
          name: "setting_value",
          value: "false",
        },
      ];
      this.updateRecords(records, attributes);
    },
    onListUpdate() {
      this.$apollo.queries.userAccessSettings.refetch();
    },
    onShowBatchUpdate() {
      this.showBatchUpdateDialog = true;
    },
    updateRecords(records, attributes) {
      this.$apollo
        .mutate({
          mutation: UPDATE_RECORDS_MUTATION,
          variables: {
            input: {
              records,
              attributes,
              model: "UserAccessSetting",
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Records Updated.",
          });
          this.showBatchUpdateDialog = false;
          this.$apollo.queries.userAccessSettings.refetch();
        });
    },
    updateUserRecords(records, attributes) {
      this.$apollo
        .mutate({
          mutation: UPDATE_RECORDS_MUTATION,
          variables: {
            input: {
              records,
              attributes,
              model: "User",
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Records Updated.",
          });
          this.showBatchUpdateDialog = false;
          this.$apollo.queries.userAccessSettings.refetch();
        });
    },
    onUpdateRecords(payload) {
      const records = this.selected.map((item) => item.id);
      const attributes = payload.map((field) => {
        return {
          name: field.name,
          value: field.value.toString(),
        };
      });
      this.updateRecords(records, attributes);
    },
  },

  apollo: {
    userAccessSettings: {
      query: LIST_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: {
            ...this.searchParams,
          },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.userAccessSettings.totalCount;
      },
    },
  },
};
</script>

<style>
#ag-user-access-settings-list {
  height: calc(100vh - 320px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/plugins/user-access-settings/UserAccessSettings.vue